import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CountryDto, UserDto, UserInformationSetCommand } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ESalutation } from 'projects/shared/lib/enum/salutation/salutation.enum';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { IAddressServiceToken } from 'projects/shared/lib/services/data/address/address-service.interface';
import { CheckNewsletterComponent } from 'projects/shared/lib/components/shared/check/newsletter/check-newsletter.component';
import { CheckSendConfirmationComponent } from 'projects/shared/lib/components/shared/check/send-confirmation/check-send-confirmation.component';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-user-information-set-dialog',
  imports: [
    FormsModule,
    CheckNewsletterComponent,
    CheckSendConfirmationComponent
  ],
  templateUrl: './user-information-set-dialog.component.html',
  styleUrls: ['./user-information-set-dialog.component.scss']
})
export class UserInformationSetDialogComponent implements OnInit {
  @Input() public modalTitle?: string;
  @Input() public submitButtonLabel: string;
  @Input() public cancelButtonLabel: string;
  @Input() public user: UserDto | undefined;

  public userCopy!: UserDto;

  public displayNewsletterSubscription: boolean = ENVIRONMENT.user.displayNewsletterSubscription != undefined ? ENVIRONMENT.user.displayNewsletterSubscription : true;

  @Output() public modalSubmit: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() public modalCancel: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  protected readonly userService: IUserService;
  protected logger: LoggerService;
  protected readonly toastService: IToastService = inject(IToastServiceToken);

  public inputValidationEmailRegexPattern: string;
  public inputValidationMobileRegexPattern: string;
  public eSalutation: typeof ESalutation;
  public countryList: CountryDto[] | undefined;
  public selectedCountryCode: string | undefined;
  public selectedCountry: CountryDto | undefined;
  protected mobileNumberWithoutDialingCode: string | undefined;

  public constructor() {
    this.userService = inject(IUserServiceToken);
    this.logger = ServiceLocator.injector.get(LoggerService);

    this.submitButtonLabel = $localize`:@@UserInformationSetDialogSubmitButtonDefaultLabel:Accept`;
    this.cancelButtonLabel = $localize`:@@UserInformationSetDialogCancelButtonDefaultLabel:Cancel`;

    this.inputValidationEmailRegexPattern = ENVIRONMENT.validation.inputValidationEmailRegexPattern;
    this.inputValidationMobileRegexPattern = ENVIRONMENT.validation.inputValidationMobileRegexPattern;
    this.eSalutation = ESalutation;
    this.countryList = [];
  }

  async ngOnInit() {
    this.userCopy = new UserDto(this.user);
    await this.countyListGetAsync();
    if (this.countryList != undefined && this.countryList.length > 0) {
      this.countryList.forEach(country => {
        if (country.dialingCode != undefined && this.userCopy.mobile?.includes(country.dialingCode)) {
          this.selectedCountry = country;
          this.selectedCountryCode = country.dialingCode;
          this.mobileNumberWithoutDialingCode = this.userCopy.mobile?.slice(this.selectedCountryCode?.length, this.userCopy.mobile.length);
          if (country.countryPhoneNumberRegex != undefined && ENVIRONMENT.validation.useCountrySpecificMobileValidation) {
            this.inputValidationMobileRegexPattern = country.countryPhoneNumberRegex;
          }
        }
      });
      if (this.selectedCountryCode == undefined) {
        this.selectedCountry = this.countryList[0];
        this.selectedCountryCode = this.countryList[0].dialingCode;
      }
      if (this.mobileNumberWithoutDialingCode == undefined) {
        this.mobileNumberWithoutDialingCode = "";
      }
    }
  }

  public async countyListGetAsync() {
    await firstValueFrom(ServiceLocator.injector.get(IAddressServiceToken).countryListGet(true)).then((data) => {
      if (!data.countryList || data.countryList.length < 1) {
        return;
      }
      this.countryList = data.countryList;
    }).catch((error) => {
      ServiceLocator.injector.get(LoggerService).warn(error, this.constructor.name);
      let errorMessage = $localize`:@@UserInformationSetDialogBaseCountryLoadErrorMessage:Could not load countrys`;
      throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
    });
  }

  public async submitAsync(event: MouseEvent) {
    // validation
    let form = document.getElementById('userInformationSetDialog') as HTMLFormElement;
    if (form.checkValidity() === false) {
      form.classList.add('was-validated');
      return;
    }

    // valid
    await this.userInformationSetAsync(this.userCopy).then((success) => {
      if (success) {
        this.user = this.userCopy;
        this.modalSubmit.emit(event);
      }
    }).catch((ex) => {
      let errorMessage = $localize`:@@UserInformationSetDialogSaveFailedMessage:Save Failed`;
      throw new BasicError(errorMessage, JSON.parse(ex.response).errorCode);
    });
  }

  public cancel(event: MouseEvent) {
    this.modalCancel.emit(event);
  }

  protected async userInformationSetAsync(user: UserDto): Promise<boolean> {
    let userInformationSetCommand: UserInformationSetCommand = new UserInformationSetCommand();
    userInformationSetCommand.eMail = user.eMail;
    userInformationSetCommand.userAlias = user.userAlias;
    userInformationSetCommand.confirmationIsPreselected = user.confirmationIsPreselected ? user.confirmationIsPreselected : false;
    userInformationSetCommand.newsletterSubscribed = user.newsletterSubscribed ? user.newsletterSubscribed : false;
    userInformationSetCommand.mobile = undefined;
    if (this.userCopy?.mobile != undefined) {
      userInformationSetCommand.mobile = this.userCopy?.mobile;
    }

    try {
      let result = await firstValueFrom(this.userService.userInformationSet(userInformationSetCommand));
      if (result) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      this.logger.error(error, this.constructor.name);
      return false;
    }

  }
}
